import { useState, useEffect, useMemo } from 'react';
import { ChevronLeft, ChevronRight, TrendingUp, Calendar } from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';
import { Card, CardHeader, CardContent, Select, LoadingSpinner } from '../components/ui';
import { getProjection, getUsers, getSettings } from '../services/api';
import type { ProjectionMonth, User } from '../types';
import { useAuth } from '../contexts/AuthContext';

export default function Projection() {
  const { user } = useAuth();
  const isRootAdmin = Boolean(user?.isRootAdmin);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [monthlyBreakdown, setMonthlyBreakdown] = useState<ProjectionMonth[]>([]);
  const [defaultCurrency, setDefaultCurrency] = useState('INR');
  const [inrRates, setInrRates] = useState<Record<string, number>>({});

  useEffect(() => {
    fetchUsers();
    fetchSettings();
  }, []);

  useEffect(() => {
    fetchProjection();
  }, [year, selectedUser]);

  useEffect(() => {
    const currenciesToFetch = Array.from(new Set([
      ...monthlyBreakdown.flatMap((m) => m.subscriptions.map((s) => s.currency)),
      defaultCurrency,
    ].filter((code) => code && code !== 'INR')));

    fetchInrRates(currenciesToFetch);
  }, [monthlyBreakdown, defaultCurrency]);

  const fetchUsers = async () => {
    try {
      const response = await getUsers();
      if (response.success) {
        const filterUsers = (!isRootAdmin && user)
          ? [{
              id: user.id,
              name: `${user.name} (Me)`,
              email: user.email,
              role: user.role,
              active: user.active,
              createdAt: user.createdAt,
              updatedAt: user.updatedAt,
            }, ...response.users]
          : response.users;
        setUsers(filterUsers);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchProjection = async () => {
    try {
      setLoading(true);
      const response = await getProjection(year.toString(), selectedUser || undefined);
      if (response.success) {
        setMonthlyBreakdown(response.monthlyBreakdown);
      }
    } catch (error) {
      console.error('Failed to fetch projection:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await getSettings();
      if (response.success && response.settings?.currency) {
        setDefaultCurrency(response.settings.currency);
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const fetchInrRates = async (currenciesToFetch: string[]) => {
    if (!currenciesToFetch.length) {
      setInrRates({});
      return;
    }

    const fetchRateToInr = async (code: string): Promise<number | null> => {
      try {
        const primaryResponse = await fetch(`https://api.frankfurter.app/latest?from=${code}&to=INR`);
        if (primaryResponse.ok) {
          const primaryData = await primaryResponse.json() as { rates?: Record<string, number> };
          const primaryRate = primaryData?.rates?.INR;
          if (typeof primaryRate === 'number') {
            return primaryRate;
          }
        }
      } catch {
      }

      try {
        const fallbackResponse = await fetch(`https://open.er-api.com/v6/latest/${code}`);
        if (!fallbackResponse.ok) return null;
        const fallbackData = await fallbackResponse.json() as { rates?: Record<string, number> };
        const fallbackRate = fallbackData?.rates?.INR;
        return typeof fallbackRate === 'number' ? fallbackRate : null;
      } catch {
        return null;
      }
    };

    const results = await Promise.all(
      currenciesToFetch.map(async (code) => {
        const rate = await fetchRateToInr(code);
        return typeof rate === 'number' ? { code, rate } : null;
      })
    );

    const rateMap = results.reduce<Record<string, number>>((acc, item) => {
      if (item) {
        acc[item.code] = item.rate;
      }
      return acc;
    }, {});

    setInrRates(rateMap);
  };

  const convertAmount = (amount: number, fromCurrency: string, toCurrency: string) => {
    if (!fromCurrency || !toCurrency || fromCurrency === toCurrency) {
      return amount;
    }

    if (fromCurrency === 'INR') {
      const toCurrencyInrRate = inrRates[toCurrency];
      if (typeof toCurrencyInrRate === 'number' && toCurrencyInrRate > 0) {
        return amount / toCurrencyInrRate;
      }
      return amount;
    }

    if (toCurrency === 'INR') {
      const fromCurrencyInrRate = inrRates[fromCurrency];
      if (typeof fromCurrencyInrRate === 'number') {
        return amount * fromCurrencyInrRate;
      }
      return amount;
    }

    const fromCurrencyInrRate = inrRates[fromCurrency];
    const toCurrencyInrRate = inrRates[toCurrency];
    if (
      typeof fromCurrencyInrRate === 'number' &&
      typeof toCurrencyInrRate === 'number' &&
      toCurrencyInrRate > 0
    ) {
      return (amount * fromCurrencyInrRate) / toCurrencyInrRate;
    }

    return amount;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: defaultCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const convertedMonthlyBreakdown = useMemo(() => {
    return monthlyBreakdown.map((month) => {
      const total = month.subscriptions.reduce(
        (acc, sub) => acc + convertAmount(sub.amount, sub.currency, defaultCurrency),
        0
      );
      return {
        ...month,
        total,
      };
    });
  }, [monthlyBreakdown, inrRates, defaultCurrency]);

  const chartData = convertedMonthlyBreakdown.map((m) => ({
    name: m.monthName.slice(0, 3),
    total: m.total,
    fullName: m.monthName,
  }));

  const yearlyTotal = convertedMonthlyBreakdown.reduce((acc, m) => acc + m.total, 0);
  const avgMonthly = yearlyTotal / 12;
  const currentMonth = new Date().getMonth();
  const ytdTotal = convertedMonthlyBreakdown
    .slice(0, currentMonth + 1)
    .reduce((acc, m) => acc + m.total, 0);

  if (loading && !monthlyBreakdown.length) {
    return (
      <div className="h-full flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Projection</h1>
          <p className="text-dark-400 mt-1">Yearly subscription forecast</p>
        </div>
        
        <div className="flex items-center gap-3">
          <Select
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
            options={[
              { value: '', label: isRootAdmin ? 'All Families' : 'All Members' },
              ...users.map((u) => ({ value: u.id, label: u.name })),
            ]}
            className="w-40"
          />
          
          <div className="flex items-center bg-dark-800 border border-dark-700 rounded-lg">
            <button
              onClick={() => setYear(year - 1)}
              className="p-2 text-dark-400 hover:text-white transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="px-4 text-white font-medium min-w-[80px] text-center">
              {year}
            </span>
            <button
              onClick={() => setYear(year + 1)}
              className="p-2 text-dark-400 hover:text-white transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="flex items-center gap-4">
            <div className="w-12 h-12 bg-primary-500/20 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-primary-400" />
            </div>
            <div>
              <p className="text-dark-400 text-sm">Yearly Total</p>
              <p className="text-2xl font-bold text-white">{formatCurrency(yearlyTotal)}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
              <Calendar className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <p className="text-dark-400 text-sm">Monthly Average</p>
              <p className="text-2xl font-bold text-white">{formatCurrency(avgMonthly)}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-4">
            <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <p className="text-dark-400 text-sm">Year to Date</p>
              <p className="text-2xl font-bold text-white">{formatCurrency(ytdTotal)}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Area Chart */}
        <Card>
          <CardHeader>
            <h2 className="text-lg font-semibold text-white">Monthly Projection</h2>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="name" stroke="#94a3b8" fontSize={12} />
                  <YAxis stroke="#94a3b8" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1e293b',
                      border: '1px solid #334155',
                      borderRadius: '8px',
                    }}
                    labelFormatter={(_, payload) => payload[0]?.payload?.fullName || ''}
                    formatter={(value: number) => [formatCurrency(value), 'Total']}
                  />
                  <Area
                    type="monotone"
                    dataKey="total"
                    stroke="#3b82f6"
                    fill="url(#colorTotal)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Bar Chart */}
        <Card>
          <CardHeader>
            <h2 className="text-lg font-semibold text-white">Monthly Comparison</h2>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="name" stroke="#94a3b8" fontSize={12} />
                  <YAxis stroke="#94a3b8" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1e293b',
                      border: '1px solid #334155',
                      borderRadius: '8px',
                    }}
                    labelFormatter={(_, payload) => payload[0]?.payload?.fullName || ''}
                    formatter={(value: number) => [formatCurrency(value), 'Total']}
                  />
                  <Bar dataKey="total" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Details Table */}
      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold text-white">Monthly Breakdown</h2>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-dark-700">
                  <th className="px-6 py-3 text-left text-xs font-medium text-dark-400 uppercase tracking-wider">
                    Month
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-dark-400 uppercase tracking-wider">
                    Active Subscriptions
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-dark-400 uppercase tracking-wider">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-dark-700">
                {convertedMonthlyBreakdown.map((month, index) => (
                  <tr
                    key={month.month}
                    className={`hover:bg-dark-700/50 transition-colors ${
                      index === currentMonth && year === new Date().getFullYear()
                        ? 'bg-primary-500/10 border-l-2 border-l-primary-500'
                        : ''
                    }`}
                  >
                    <td className="px-6 py-4">
                      <div className="text-white font-medium">{month.monthName}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-2">
                        {month.subscriptions.length > 0 ? (
                          month.subscriptions.slice(0, 5).map((sub) => (
                            <span
                              key={sub.id}
                              className="px-2 py-1 text-xs bg-dark-700 text-dark-300 rounded"
                            >
                              {sub.name}
                            </span>
                          ))
                        ) : (
                          <span className="text-dark-500 text-sm">No subscriptions</span>
                        )}
                        {month.subscriptions.length > 5 && (
                          <span className="px-2 py-1 text-xs bg-dark-700 text-dark-400 rounded">
                            +{month.subscriptions.length - 5} more
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className="text-white font-medium">
                        {formatCurrency(month.total)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-dark-600 bg-dark-700/50">
                  <td colSpan={2} className="px-6 py-4 text-white font-semibold">
                    Total for {year}
                  </td>
                  <td className="px-6 py-4 text-right text-white font-bold text-lg">
                    {formatCurrency(yearlyTotal)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
